import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { personasAPI } from '@/lib/api';

const EMPTY_IDENTITY = {
  userId: null,
  employeeCode: null,
  name: null,
  designation: null,
  department: null,
  branch: null,
  unit: null,
  grade: null,
  reportingManager: null,
};

const EMPTY = {
  persona: null,
  dashboardRoute: null,
  capabilities: [],
  allowedMenuKeys: [],
  blockedMenuKeys: [],
  identity: EMPTY_IDENTITY,
  loading: true,
};

const PersonaContext = createContext(EMPTY);

/**
 * Fetches the user's persona once per session. The persona is the BD-specific
 * sub-classification on top of role; the user-facing identity (name,
 * designation, department) comes from the imported employee master and is
 * returned alongside the persona key so dashboards can render it directly.
 */
export const PersonaProvider = ({ children }) => {
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setState({ ...EMPTY, loading: false });
        return;
      }
      try {
        const data = await personasAPI.getMine();
        if (cancelled) return;
        const id = data.identity || {};
        setState({
          persona: data.persona,
          dashboardRoute: data.dashboard_route,
          capabilities: data.capabilities || [],
          allowedMenuKeys: data.allowed_menu_keys || [],
          blockedMenuKeys: data.blocked_menu_keys || [],
          identity: {
            userId: id.user_id ?? null,
            employeeCode: id.employee_code ?? null,
            name: id.name ?? null,
            designation: id.designation ?? null,
            department: id.department ?? null,
            branch: id.branch ?? null,
            unit: id.unit ?? null,
            grade: id.grade ?? null,
            reportingManager: id.reporting_manager ?? null,
          },
          loading: false,
        });
      } catch {
        if (!cancelled) setState({ ...EMPTY, loading: false });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return <PersonaContext.Provider value={state}>{children}</PersonaContext.Provider>;
};

export const usePersona = () => {
  const ctx = useContext(PersonaContext);
  return useMemo(() => {
    const capSet = new Set(ctx.capabilities || []);
    const blockedSet = new Set(ctx.blockedMenuKeys || []);
    const allowedSet = new Set(ctx.allowedMenuKeys || []);
    return {
      ...ctx,
      hasCapability: (cap) => capSet.has(cap),
      hasAnyCapability: (caps) => caps.some((c) => capSet.has(c)),
      isMenuAllowed: (menuKey) => {
        // No persona → don't override role-based behaviour (return null,
        // caller treats null as "fall back to role").
        if (!ctx.persona) return null;
        if (blockedSet.has(menuKey)) return false;
        if (allowedSet.size > 0) return allowedSet.has(menuKey);
        return true;
      },
    };
  }, [ctx]);
};

export default usePersona;
